import { ReactNode, useMemo } from 'react'
import { useCharacterClasses } from 'app/frontend/base/hooks/useCharacterClasses'
import { TeamEventParticipantsValueProvider } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/context/TeamEventParticipantsContext'
import { TeamEventPlanningInfoProvider } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/context/TeamEventPlanningInfoContext'
import {
  TeamEventUpdateProvider,
  TeamEventUpdateContextValue,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/context/TeamEventUpdateContext'
import { EventActivityParticipantEntry } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/eventActivityParticipantEntry'
import { resolveEventActivityIssues } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/eventActivityIssues'

type Props = {
  activity: wowaudit.EventActivity
  entries: EventActivityParticipantEntry[]
  onUpdateSelection: TeamEventUpdateContextValue['updateSelection']
  children: ReactNode
}

const noopAsync = async () => undefined
const noop = () => undefined

const DefaultSetupProviders = ({
  activity,
  entries,
  onUpdateSelection,
  children,
}: Props) => {
  const characterClasses = useCharacterClasses()
  const activityIssuesById = useMemo(() => {
    const issues = resolveEventActivityIssues({
      activity,
      entries,
      unavailableStatuses: [],
      characterClasses,
    })
    return new Map([[activity.id, issues]])
  }, [activity, characterClasses, entries])

  const participantsValue = useMemo(
    () => ({
      event: {
        id: 0,
        activities: [activity],
        activityType: 'raids' as const,
        autoAddParticipants: true,
        date: '',
        effectiveGuestPolicy: 'disabled',
        endsAt: '',
        finishTime: '',
        instances: activity.instance ? [activity.instance] : [],
        optional: false,
        participantReferences: [],
        participants: entries.map((entry) => entry.participant),
        path: '',
        primaryActivityId: activity.id,
        raidsPlanningScope: 'single' as const,
        siblings: {},
        startTime: '',
        startsAt: '',
        status: 'planned' as const,
        touched: false,
      } as wowaudit.Event,
      allEntries: entries,
      activityIssuesById,
      activeActivityIds: new Set([activity.id]),
      planningInfoByActivityAndCharacter: new Map(),
      characterDetailsById: {},
    }),
    [activity, activityIssuesById, entries],
  )

  const updateValue = useMemo<TeamEventUpdateContextValue>(
    () => ({
      isSaving: false,
      editEvent: noopAsync,
      updateEvent: noopAsync,
      updateStatus: noop,
      updateAdminComment: noop,
      updateSelection: onUpdateSelection,
      updateActivitySelectionForAllParticipants: noop,
      updateSelectionForAllActivities: noop,
      updateEventActivity: noopAsync,
      copySelectionsFromActivity: noopAsync,
      exportEventActivityOverrides: noopAsync,
      updateEventActivityOrder: noopAsync,
      addEventActivities: noopAsync,
      removeEventActivity: noopAsync,
      importEventFromPreviousEvent: noopAsync,
      resetAllSelections: noopAsync,
      setAllActivitiesActive: noopAsync,
      removeGuest: noop,
    }),
    [onUpdateSelection],
  )

  return (
    <TeamEventParticipantsValueProvider value={participantsValue}>
      <TeamEventPlanningInfoProvider
        value={{
          showPlanningInfo: false,
          setShowPlanningInfo: noop,
        }}
      >
        <TeamEventUpdateProvider value={updateValue}>
          {children}
        </TeamEventUpdateProvider>
      </TeamEventPlanningInfoProvider>
    </TeamEventParticipantsValueProvider>
  )
}

export default DefaultSetupProviders
